import React, { useRef, useState, useEffect } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import { Link } from 'react-router-dom';
import portfolioData from '../data/portfolioData';
import { usePerformance } from '../hooks/usePerformance';

const Portfolio = () => {
    const containerRef = useRef(null);
    const controls = useAnimationControls();
    const indexRef = useRef(0);
    const [isHovered, setIsHovered] = useState(false);
    const { isMobile, prefersReducedMotion, isLowEndDevice } = usePerformance();

    const totalItems = portfolioData.length;

    useEffect(() => {
        // Disable auto-slide on mobile and reduced motion for better performance
        if (isHovered || isMobile || prefersReducedMotion) return;

        const interval = setInterval(() => {
            indexRef.current = (indexRef.current + 1) % totalItems;
            controls.start({
                x: `-${indexRef.current * 100}%`,
                transition: {
                    duration: 1,
                    ease: [0.4, 0, 0.2, 1],
                    type: "tween",
                }
            });
        }, 3000); // Change image every 3 seconds

        return () => clearInterval(interval);
    }, [isHovered, controls, totalItems, isMobile, prefersReducedMotion]);

    // Initialize position
    useEffect(() => {
        controls.set({ x: '0%' });
    }, [controls]);

    return (
        <div 
            ref={containerRef}
            className="w-full bg-white/50 dark:bg-black/50 backdrop-blur-md overflow-hidden min-h-screen relative py-0 md:py-20" 
            id='portfolio'
        >
            {/* Vibrant animated background */}
            {!prefersReducedMotion && (() => {
                const orbCount = isMobile || isLowEndDevice ? 3 : 4; // Reduced from 6
                const blurAmount = isMobile || isLowEndDevice ? '40px' : '70px';
                
                return (
                    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
                        {/* Dynamic floating particles */}
                        {Array.from({ length: orbCount }).map((_, i) => (
                            <motion.div
                                key={i}
                                className="absolute rounded-full"
                                style={{
                                    width: `${200 + i * 90}px`,
                                    height: `${200 + i * 90}px`,
                                    background: `radial-gradient(circle, ${
                                        i % 3 === 0 
                                            ? 'rgba(251, 146, 60, 0.7)' 
                                            : i % 3 === 1
                                            ? 'rgba(168, 85, 247, 0.7)'
                                            : 'rgba(236, 72, 153, 0.7)'
                                    } 0%, transparent 70%)`,
                                    filter: `blur(${blurAmount})`,
                                    left: `${10 + i * 15}%`,
                                    top: `${15 + i * 14}%`,
                                    opacity: 0.45,
                                    willChange: 'transform, opacity',
                                    contain: 'layout style paint',
                                    transform: 'translateZ(0)',
                                }}
                                animate={{
                                    x: [0, 55, -50, 0],
                                    y: [0, -55, 50, 0],
                                    scale: isMobile ? [1, 1.3, 0.85, 1] : [1, 1.5, 0.75, 1],
                                    opacity: [0.45, 0.65, 0.3, 0.45],
                                }}
                                transition={{
                                    duration: 15 + i * 2,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: i * 1.5,
                                    type: "tween",
                                }}
                            />
                        ))}
                    </div>
                );
            })()}
            {/* Mobile: 4 images stacked vertically, full screen, no borders */}
            <div className="md:hidden w-full h-screen relative z-10">
                <div className="flex flex-col h-full">
                    {portfolioData.slice(0, 4).map((project, index) => {
                        const imageUrl = `https://picsum.photos/seed/${project.imageId || project.id}/1920/1280`;
                        
                        return (
                            <div
                                key={project.id}
                                className="flex-1 relative overflow-hidden"
                            >
                                <img 
                                    src={imageUrl}
                                    alt={project.title}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    srcSet={`${imageUrl} 1920w, https://picsum.photos/seed/${project.imageId || project.id}/1280/853 1280w, https://picsum.photos/seed/${project.imageId || project.id}/800/533 800w`}
                                    sizes="100vw"
                                />
                            </div>
                        );
                    })}
                </div>
                
                {/* Centered button overlay */}
                <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
                    <Link 
                        to="/portfolio"
                        className="pointer-events-auto bg-white/90 dark:bg-black/90 backdrop-blur-md text-gray-900 dark:text-white font-black text-lg px-8 py-4 rounded-xl shadow-2xl hover:bg-white dark:hover:bg-black transition-all duration-300 hover:scale-105 active:scale-95"
                    >
                        Voir le Portfolio
                    </Link>
                </div>
            </div>

            {/* Desktop: Auto-sliding carousel */}
            <div className="hidden md:block relative h-full w-full z-10">
                <motion.div 
                    className="flex h-full"
                    animate={controls}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {portfolioData.map((project, index) => {
                        const imageUrl = `https://picsum.photos/seed/${project.imageId || project.id}/2560/1440`;
                        
                        return (
                            <motion.div
                                key={`${project.id}-${index}`}
                                className="h-full flex-shrink-0 w-screen"
                            >
                                <div className="w-full h-full relative overflow-hidden">
                                    <motion.img 
                                        src={imageUrl}
                                        alt={project.title}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                        srcSet={`${imageUrl} 2560w, https://picsum.photos/seed/${project.imageId || project.id}/1920/1080 1920w, https://picsum.photos/seed/${project.imageId || project.id}/1280/720 1280w`}
                                        sizes="100vw"
                                    />
                                    <motion.div 
                                        className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end justify-center p-8"
                                        initial={{ opacity: 0 }}
                                        whileHover={{ opacity: 1 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <h3 className="text-white text-3xl md:text-5xl font-bold text-center">{project.title}</h3>
                                    </motion.div>
                                </div>
                            </motion.div>
                        );
                    })}
                </motion.div>
            </div>
        </div>
    )
}

export default Portfolio;